import graphSnapshot from '../data/graph-snapshot.json';
import productServiceMapping from '../data/product-service-mapping.json';

interface GraphEntry {
  name: string;
  dependencies: { name: string; default: boolean; required?: string[] }[] | null;
}

export interface ServiceDef {
  name: string;
  displayName: string;
}

interface ProductDef {
  displayName: string;
  type: string;
  partialSelection: boolean;
  services: ServiceDef[];
}

const products = (productServiceMapping as any).products as Record<string, ProductDef>;

// Build fabric service set once
const fabricServices = new Set<string>();
Object.keys(products).forEach((key) => {
  const def = products[key];
  if (def.type === 'fabric') {
    def.services.forEach((svc) => fabricServices.add(svc.name));
  }
});

// Build reverse lookup: service → product key
const serviceToProduct = new Map<string, string>();
Object.keys(products).forEach((productKey) => {
  const def = products[productKey];
  def.services.forEach((svc) => {
    serviceToProduct.set(svc.name, productKey);
  });
});

// Virtual services that share dependency graphs with real graph services.
// DU Modern Projects uses the same helm chart as classic DU (documentunderstanding)
// with modernProjects.enabled=true, so it has the same infrastructure dependencies.
const SERVICE_ALIASES: Record<string, string> = {
  'du-modern': 'documentunderstanding',
};

/**
 * Returns the set of active feature flags implied by the selected products.
 * E.g. selecting DU AI Center activates the "classic_projects" flag,
 * which gates the documentunderstanding → aicenter conditional edge.
 */
function getActiveFlags(productKeys: string[]): Set<string> {
  const flags = new Set<string>();
  if (productKeys.includes('document_understanding_ai_center')) {
    flags.add('classic_projects');
  }
  return flags;
}

/**
 * BFS-expand from seed services through the adjacency map, skipping fabric services.
 * Returns the set of all reachable services (including seeds).
 * When a service has no adjacency entry, checks SERVICE_ALIASES and expands the
 * alias's deps instead (the original service stays in the result for resource lookup).
 */
function bfsExpand(
  seeds: string[],
  adj: Record<string, string[]>,
  fabSvcs: Set<string>,
  visited?: Set<string>
): Set<string> {
  const result = visited || new Set<string>();
  const queue = [...seeds];
  seeds.forEach((s) => result.add(s));
  while (queue.length > 0) {
    const svc = queue.shift()!;
    // If the service isn't in the adjacency map, try its alias
    const lookupSvc = (adj[svc] !== undefined) ? svc : (SERVICE_ALIASES[svc] || svc);
    const deps = adj[lookupSvc] || [];
    for (const dep of deps) {
      if (result.has(dep)) continue;
      if (fabSvcs.has(dep)) continue;
      result.add(dep);
      queue.push(dep);
    }
  }
  return result;
}

/**
 * Build the service adjacency map for a distribution from graph-snapshot.
 * Edges with default:false are included only when their `required` flags
 * intersect with activeFlags.
 */
function buildAdjacencyMap(distribution: string, activeFlags: Set<string>): Record<string, string[]> {
  const profiles = (graphSnapshot as any).profiles[distribution] as GraphEntry[];
  if (!profiles) return {};

  const adj: Record<string, string[]> = {};
  for (const entry of profiles) {
    adj[entry.name] = entry.dependencies
      ? entry.dependencies
          .filter((d: any) => d.default !== false || (d.required && d.required.some((r: string) => activeFlags.has(r))))
          .map((d: any) => d.name)
      : [];
  }
  return adj;
}

/**
 * Builds a REVERSE service-dependency adjacency for a distribution:
 * `rev[S]` = the list of services that depend on `S` (default edges only,
 * no active flags — i.e. the always-on dependency structure).
 *
 * Used by the A/P service-ban closure (apSupportAdapter): a ban on a service
 * must propagate to everything that depends on it. `resolveServices` and its
 * forward adjacency are deliberately left untouched — this is a pure,
 * independent inversion of the same graph data.
 */
export function getReverseServiceAdjacency(distribution: string): Record<string, string[]> {
  const adj = buildAdjacencyMap(distribution, new Set<string>());
  const rev: Record<string, string[]> = {};
  for (const svc of Object.keys(adj)) {
    if (!rev[svc]) rev[svc] = [];
    for (const dep of adj[svc]) {
      (rev[dep] = rev[dep] || []).push(svc);
    }
  }
  return rev;
}

export interface ResolveResult {
  resolvedServices: string[];
  productServices: Map<string, string[]>;
  autoSelectedProducts: string[];
}

/**
 * Resolves the full set of services needed for the given explicit product keys,
 * using BFS through the graph dependency chain. Fabric services are excluded
 * from expansion (they act as leaf nodes).
 */
export function resolveServices(
  explicitProductKeys: string[],
  distribution: string
): ResolveResult {
  const activeFlags = getActiveFlags(explicitProductKeys);
  const adj = buildAdjacencyMap(distribution, activeFlags);
  const explicitSet = new Set(explicitProductKeys);

  // Seed BFS with all services from explicit products, then expand deps
  const seeds: string[] = [];
  for (const productKey of explicitProductKeys) {
    const def = products[productKey];
    if (!def) continue;
    for (const svc of def.services) {
      if (seeds.indexOf(svc.name) === -1) {
        seeds.push(svc.name);
      }
    }
  }
  const visited = bfsExpand(seeds, adj, fabricServices);

  // Classify resolved services by owning product
  const productServicesMap = new Map<string, string[]>();
  Array.from(visited).forEach((svc) => {
    const owner = serviceToProduct.get(svc);
    if (!owner) return; // Unowned services (infra not in any product)
    const existing = productServicesMap.get(owner) || [];
    existing.push(svc);
    productServicesMap.set(owner, existing);
  });

  // Determine final resolved services per product
  const finalServices = new Set<string>();
  const autoSelectedProducts: string[] = [];

  const productKeys = Array.from(productServicesMap.keys());
  for (const productKey of productKeys) {
    const resolvedSvcs = productServicesMap.get(productKey)!;
    const def = products[productKey];
    if (!def || def.type === 'fabric') continue;

    const isExplicit = explicitSet.has(productKey);
    if (!isExplicit) {
      autoSelectedProducts.push(productKey);
    }

    // Explicit products or partialSelection=false → include ALL product services
    // partialSelection=true auto-selected → include only resolved services
    if (isExplicit || !def.partialSelection) {
      def.services.forEach((svc) => finalServices.add(svc.name));
    } else {
      resolvedSvcs.forEach((svc) => finalServices.add(svc));
    }
  }

  // Rebuild productServices map with final services
  const finalProductServices = new Map<string, string[]>();
  Array.from(finalServices).forEach((svc) => {
    const owner = serviceToProduct.get(svc);
    if (!owner) return;
    const existing = finalProductServices.get(owner) || [];
    existing.push(svc);
    finalProductServices.set(owner, existing);
  });

  return {
    resolvedServices: Array.from(finalServices),
    productServices: finalProductServices,
    autoSelectedProducts,
  };
}

/**
 * Returns a copy of a `ResolveResult` with the given services removed. Pure and
 * selection-agnostic: it takes an arbitrary exclusion set and knows nothing
 * about A/P, deploymentType, or `ap-support.json` — so `resolveServices` stays
 * a vanilla, UI-facing resolver.
 *
 * Used by the A/P secondary pass to drop banned services that a
 * `partialSelection` owner (e.g. `platform` owning `taas`) force-included via
 * an explicit key. The product-level secondary filter cannot express "keep the
 * product, drop one service" — this can.
 *
 * Empty owner entries are dropped from `productServices` (not kept as `[]`), so
 * the map shape stays indistinguishable from a natural resolution.
 * `autoSelectedProducts` passes through unchanged (product-level; excluded
 * products are already removed from the secondary formData upstream).
 */
export function stripServices(
  result: ResolveResult,
  exclude: Set<string>
): ResolveResult {
  if (exclude.size === 0) return result;
  const productServices = new Map<string, string[]>();
  Array.from(result.productServices.entries()).forEach(([key, svcs]) => {
    const kept = svcs.filter((s) => !exclude.has(s));
    if (kept.length > 0) productServices.set(key, kept);
  });
  return {
    resolvedServices: result.resolvedServices.filter((s) => !exclude.has(s)),
    productServices,
    autoSelectedProducts: result.autoSelectedProducts,
  };
}

/**
 * Rebuilds a `products` array so it matches what the current dependency graph
 * would resolve from the given `explicitKeys`. Existing product entries are
 * preserved (so user-edited properties like concurrent counts survive); newly
 * required auto-deps are appended from `allProductOptions`.
 *
 * Needed because shared URLs persist a snapshot of `products` from the time
 * of save — when the graph later grows new edges (e.g. agents → studioweb),
 * those URLs would otherwise render and compute against a stale product list
 * even though the modal correctly surfaces the new auto-deps.
 */
export function reconcileProductsWithGraph(
  explicitKeys: string[],
  existingProducts: ProductOptionInterface[],
  distribution: string,
  allProductOptions: ProductOptionInterface[]
): ProductOptionInterface[] {
  const resolved = resolveServices(explicitKeys, distribution);
  const required = new Set<string>(explicitKeys);
  resolved.autoSelectedProducts.forEach(k => required.add(k));

  const existingKeys = new Set(existingProducts.map(p => p.key));
  const result: ProductOptionInterface[] = existingProducts.filter(p => required.has(p.key));

  required.forEach(key => {
    if (!existingKeys.has(key)) {
      const product = allProductOptions.find(p => p.key === key);
      // Push a copy, not the shared constant object. Properties stay SPARSE —
      // absent numeric fields mean "at reference" (effective read path resolves them).
      if (product) result.push({ ...product, properties: { ...(product.properties || {}) } });
    }
  });

  return result;
}

// Cache for getProductDependents
const dependentsCache = new Map<string, Map<string, string[]>>();

/**
 * Returns which selected products would break if productKey were removed.
 * Filters out circular dependencies (same logic as getNonCircularDependents).
 */
export function getProductDependents(
  productKey: string,
  selectedProductKeys: string[],
  distribution: string
): string[] {
  const cacheKey = `${distribution}:${selectedProductKeys.slice().sort().join(',')}`;

  // Check cache
  let distCache = dependentsCache.get(cacheKey);
  if (distCache && distCache.has(productKey)) {
    return distCache.get(productKey)!;
  }

  const activeFlags = getActiveFlags(selectedProductKeys);
  const adj = buildAdjacencyMap(distribution, activeFlags);
  const targetServices = new Set((products[productKey]?.services || []).map((s: ServiceDef) => s.name));

  // For each other selected product, check if its dep chain touches targetServices
  const dependents: string[] = [];
  for (const otherKey of selectedProductKeys) {
    if (otherKey === productKey) continue;

    const otherDef = products[otherKey];
    if (!otherDef) continue;

    // BFS from otherKey's services
    const visited = new Set<string>();
    const otherServiceNames = otherDef.services.map((s: ServiceDef) => s.name);
    const bfsQueue = [...otherServiceNames];
    let depends = false;

    while (bfsQueue.length > 0 && !depends) {
      const svc = bfsQueue.shift()!;
      if (visited.has(svc)) continue;
      visited.add(svc);

      if (targetServices.has(svc) && otherServiceNames.indexOf(svc) === -1) {
        depends = true;
        break;
      }

      // Resolve alias for adjacency lookup
      const lookupSvc = (adj[svc] !== undefined) ? svc : (SERVICE_ALIASES[svc] || svc);
      const deps = adj[lookupSvc] || [];
      for (const dep of deps) {
        if (!visited.has(dep) && !fabricServices.has(dep)) {
          bfsQueue.push(dep);
        }
      }
    }

    if (depends) {
      dependents.push(otherKey);
    }
  }

  // Filter out circular deps: if productKey also depends on the dependent
  const productDef = products[productKey];
  const productServiceNames = (productDef?.services || []).map((s: ServiceDef) => s.name);
  const nonCircular = dependents.filter((depKey) => {
    const depDef = products[depKey];
    if (!depDef) return true;

    // Check if productKey's BFS reaches depKey's services
    const visited = bfsExpand(productServiceNames, adj, fabricServices);

    // If productKey's dep chain reaches any of depKey's services → circular
    const depServiceNames = new Set(depDef.services.map((s: ServiceDef) => s.name));
    let isCircular = false;
    Array.from(visited).forEach((svc) => {
      if (depServiceNames.has(svc) && productServiceNames.indexOf(svc) === -1) {
        isCircular = true;
      }
    });
    return !isCircular;
  });

  // Cache the result
  if (!distCache) {
    distCache = new Map();
    dependentsCache.set(cacheKey, distCache);
  }
  distCache.set(productKey, nonCircular);

  return nonCircular;
}
